<div class="container">
  <sca-module-title class="title-test">
    <h1>Security Playground</h1>
    <span scaModuleTitlePost>Testing de @argo/security - {{ coverage }}% cobertura</span>
  </sca-module-title>

  <!-- Estado del SecurityStore -->
  <section class="card">
    <h2>Estado del SecurityStore</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      SecurityStore gestiona protección de datos con estrategia adaptativa: Base64 sin auth, AES-256 con auth
    </sca-alert>
    
    <div class="status-grid">
      <sca-card-workshop [selected]="securityStore.phase$() === 'ready'">
        <header scaCardWorkshopHeader>
          <sca-badge variant="success" [rounded]="false">{{ securityStore.phase$().toUpperCase() }}</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Fase Actual</p>
        <p scaCardWorkshopAddress>{{ securityStore.phase$() }}</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="!!securityStore.sessionDerivedKey$()">
        <header scaCardWorkshopHeader>
          <sca-badge [variant]="securityStore.sessionDerivedKey$() ? 'success' : 'warning'" [rounded]="false">SESSION KEY</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Clave de Sesión</p>
        <p scaCardWorkshopAddress>
          @if (securityStore.sessionDerivedKey$()) {
            🔑 {{ securityStore.sessionDerivedKey$()!.substring(0, 16) }}...
          } @else {
            Sin autenticar
          }
        </p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="authStore.logged()">
        <header scaCardWorkshopHeader>
          <sca-badge [variant]="authStore.logged() ? 'success' : 'warning'" [rounded]="false">AUTH</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Estado de Autenticación</p>
        <p scaCardWorkshopAddress>
          @if (authStore.logged()) {
            ✅ AES-256 activo
          } @else {
            Base64 ofuscación
          }
        </p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="!!securityStore.error$()">
        <header scaCardWorkshopHeader>
          <sca-badge [variant]="securityStore.error$() ? 'error' : 'success'" [rounded]="false">
            {{ securityStore.error$() ? 'ERROR' : 'OK' }}
          </sca-badge>
        </header>
        <p scaCardWorkshopTitle>Estado de Errores</p>
        <p scaCardWorkshopAddress>{{ securityStore.error$() || 'Funcionando correctamente' }}</p>
      </sca-card-workshop>
    </div>

    <sca-alert variant="info" style="margin-top: 1.5rem;">
      <sca-icon icon="tips_and_updates" size="24px" [weight]="300" left />
      <strong>Estrategia Adaptativa:</strong><br>
      Sin auth: Ofuscación Base64 (solo oculta en DevTools)<br>
      Con auth: AES-256 real (clave derivada de JWT: sub+iat+jti)
    </sca-alert>
  </section>

  <!-- Computed Properties -->
  <section class="card">
    <h2>Computed Properties</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      3 señales reactivas computadas del SecurityStore
    </sca-alert>
    
    <table class="properties-table">
      <thead>
        <tr>
          <th>Propiedad</th>
          <th>Tipo</th>
          <th>Valor Actual</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>sessionDerivedKey$()</code></td>
          <td>Signal&lt;string|null&gt;</td>
          <td><span [class]="securityStore.sessionDerivedKey$() ? 'value-object' : 'value-null'">{{ securityStore.sessionDerivedKey$() ? securityStore.sessionDerivedKey$()!.substring(0, 30) + '...' : 'null' }}</span></td>
          <td>Clave derivada de JWT claims (sub+iat+jti) mediante SHA256</td>
        </tr>
        <tr>
          <td><code>phase$()</code></td>
          <td>Signal&lt;CommonPhase&gt;</td>
          <td><span class="value-enum">{{ securityStore.phase$() }}</span></td>
          <td>IDLE | READY | ERROR - READY significa store listo con clave cargada</td>
        </tr>
        <tr>
          <td><code>error$()</code></td>
          <td>Signal&lt;string|null&gt;</td>
          <td><span [class]="securityStore.error$() ? 'value-warning' : 'value-null'">{{ securityStore.error$() || 'null' }}</span></td>
          <td>Último error en derivación o encriptación</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- Métodos Públicos -->
  <section class="card">
    <h2>Métodos Públicos</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      2 métodos principales para protección de datos
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="lock" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>encrypt(value: string): string</p>
        <p scaCardWorkshopAddress>
          Protege string con estrategia adaptativa:<br>
          <strong>Auth:</strong> AES-256 con crypto-ts<br>
          <strong>No auth:</strong> Base64 (btoa + encodeURIComponent)
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="lock_open" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>decrypt(encryptedValue: string): string</p>
        <p scaCardWorkshopAddress>
          Recupera string original:<br>
          <strong>Auth:</strong> Desencripta AES-256<br>
          <strong>No auth:</strong> Decodifica Base64 (atob + decodeURIComponent)
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Guards Funcionales -->
  <section class="card">
    <h2>Guards Funcionales</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      4 guards de seguridad para rutas Angular (stubs para implementar)
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="shield" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>canActivateSecurityGuard</p>
        <p scaCardWorkshopAddress>
          Guard funcional para proteger rutas con CanActivateFn<br>
          <small>Actualmente stub: return true</small>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="security" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>canActivateChildSecurityGuard</p>
        <p scaCardWorkshopAddress>
          Guard funcional para rutas hijas con CanActivateChildFn<br>
          <small>Actualmente stub: return true</small>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="exit_to_app" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>canDeactivateSecurityGuard</p>
        <p scaCardWorkshopAddress>
          Guard funcional para confirmar salida con CanDeactivateFn<br>
          <small>Actualmente stub: return true</small>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="verified_user" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>canMatchSecurityGuard</p>
        <p scaCardWorkshopAddress>
          Guard funcional para matching condicional con CanMatchFn<br>
          <small>Actualmente stub: return true</small>
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- HTTP Interceptor -->
  <section class="card">
    <h2>HTTP Interceptor</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Interceptor HTTP funcional para añadir headers de seguridad
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="http" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>securityInterceptor</p>
        <p scaCardWorkshopAddress>
          HttpInterceptorFn que añade headers X-Security-*<br>
          <small>Actualmente stub: pass-through sin modificar request</small>
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Estrategia y Configuración -->
  <section class="card">
    <h2>Estrategia y Configuración</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Funcionamiento interno del SecurityStore
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="settings" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Derivación de Clave</p>
        <p scaCardWorkshopAddress>
          Effect reactivo detecta login/logout<br>
          Extrae claims JWT: sub (usuario), iat (timestamp), jti (token ID)<br>
          Genera hash SHA256 para clave única de sesión
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="autorenew" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Rotación Automática</p>
        <p scaCardWorkshopAddress>
          Clave se regenera en cada login<br>
          Se limpia (null) en logout<br>
          Datos anteriores ya no desencriptables
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="build" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Configuración</p>
        <p scaCardWorkshopAddress>
          provideSecurityConfig() en app providers<br>
          Actualmente sin opciones configurables<br>
          Estrategia adaptativa siempre activa
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="library_books" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Dependencias</p>
        <p scaCardWorkshopAddress>
          crypto-ts: Librería AES-256 (solo en modo auth)<br>
          Web Crypto API: Para hash SHA256<br>
          btoa/atob: Para Base64 (modo no-auth)
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Arquitectura del Módulo -->
  <section class="card">
    <h2>Arquitectura del Módulo</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Estructura de archivos y componentes del módulo @argo/security
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="store" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>security.store.ts (324 líneas)</p>
        <p scaCardWorkshopAddress>
          Store principal con encriptación adaptativa<br>
          Effect para derivar clave desde AuthenticationStore<br>
          Métodos encrypt/decrypt según estrategia
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="gpp_good" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>guards/security.guard.ts (120 líneas)</p>
        <p scaCardWorkshopAddress>
          4 guards funcionales modernos<br>
          canActivate, canActivateChild, canDeactivate, canMatch<br>
          Preparados para lógica de autorización
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="swap_horiz" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>interceptors/security.interceptor.ts (28 líneas)</p>
        <p scaCardWorkshopAddress>
          HttpInterceptorFn moderno<br>
          Stub para añadir headers de seguridad<br>
          Preparado para firma de requests
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="extension" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>security.providers.ts</p>
        <p scaCardWorkshopAddress>
          provideSecurityConfig() para app.config<br>
          Inyecta SecurityStore y configuración<br>
          API modular para futuras opciones
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Casos de Uso -->
  <section class="card">
    <h2>Casos de Uso Recomendados</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Escenarios típicos donde usar el SecurityStore
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="accent" [rounded]="false">USO 1</sca-badge>
        </header>
        <p scaCardWorkshopTitle>localStorage Protegido</p>
        <p scaCardWorkshopAddress>
          Encriptar datos antes de guardar en localStorage<br>
          Ejemplo: preferencias, configuración temporal<br>
          <code>localStorage.setItem('data', securityStore.encrypt(json))</code>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="accent" [rounded]="false">USO 2</sca-badge>
        </header>
        <p scaCardWorkshopTitle>sessionStorage Protegido</p>
        <p scaCardWorkshopAddress>
          Encriptar datos sensibles en sesión<br>
          Ejemplo: carrito de compra, wizard en progreso<br>
          <code>sessionStorage.setItem('cart', securityStore.encrypt(cart))</code>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="accent" [rounded]="false">USO 3</sca-badge>
        </header>
        <p scaCardWorkshopTitle>URL Query Params Ofuscados</p>
        <p scaCardWorkshopAddress>
          Ofuscar parámetros sensibles en URL<br>
          Ejemplo: IDs de documentos, referencias<br>
          <code>?data=securityStore.encrypt(docId)</code>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="accent" [rounded]="false">USO 4</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Logs y Trazas</p>
        <p scaCardWorkshopAddress>
          Ofuscar datos personales en logs<br>
          Ejemplo: nombres, DNI, emails en debug<br>
          Cumplimiento GDPR/LOPD
        </p>
      </sca-card-workshop>
    </div>
  </section>
</div>
